import type { PublicAnnouncement } from "@/features/announce/api/announceClient";
import {
  getFetchedAnnouncementsReadThroughRevision,
  getFetchedAnnouncementsReadThroughTimestamp,
} from "@/features/announce/lib/announcementReadReceipt";
import {
  advanceAnnouncementReadRevision,
  advanceAnnouncementReadTimestamp,
  announcementTimestampEpoch,
  isUnreadAnnouncement,
  isUnreadAnnouncementRevision,
} from "@/features/announce/lib/announcementReadState";
import type { AnnouncementEvent } from "@/features/announce/mediators/announcementEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type AnnouncementStatusEvent = Extract<
  AnnouncementEvent,
  {
    type:
      | "announcement/read-requested"
      | "announcement/read-through-requested"
      | "announcement/current-list-read-requested"
      | "announcement/status-meta-synced"
      | "announcement/status-storage-synced";
  }
>;

interface SessionAnnouncementRead {
  readonly publishedAt: string | null;
  readonly articleUnreadRevision: number;
}

export interface AnnouncementStatusState {
  readonly articleTimestamp: string | null;
  readonly articleUnreadRevision: number;
  readonly canReserveMeta: boolean;
  readonly hasReleasedMetaReservation: boolean;
  readonly isMetaReady: boolean;
  readonly readAnnouncements: ReadonlyMap<number, SessionAnnouncementRead>;
  readonly readRevision: number | null;
  readonly readTimestamp: string | null;
}

export type AnnouncementStatusEffect =
  | { readonly type: "persist-read-timestamp"; readonly value: string }
  | { readonly type: "persist-read-revision"; readonly value: number };

export interface AnnouncementStatusMediator
  extends Mediator<
    AnnouncementStatusState,
    AnnouncementStatusEvent,
    AnnouncementStatusEffect
  > {
  readonly hasUnreadAnnouncements: () => boolean;
  readonly isAnnouncementMetaPending: () => boolean;
  readonly isAnnouncementRead: (
    announcement: Pick<
      PublicAnnouncement,
      "id" | "published_at" | "article_unread_revision"
    >,
  ) => boolean;
}

export function isAnnouncementStatusEvent(
  event: AnnouncementEvent,
): event is AnnouncementStatusEvent {
  return (
    event.type === "announcement/read-requested" ||
    event.type === "announcement/read-through-requested" ||
    event.type === "announcement/current-list-read-requested" ||
    event.type === "announcement/status-meta-synced" ||
    event.type === "announcement/status-storage-synced"
  );
}

export function createAnnouncementStatusMediator(
  initial: Pick<AnnouncementStatusState, "readRevision" | "readTimestamp"> & {
    readonly canReserveMeta: boolean;
  },
  runEffect: (effect: AnnouncementStatusEffect) => void,
): AnnouncementStatusMediator {
  const mediator = createMediator<
    AnnouncementStatusState,
    AnnouncementStatusEvent,
    AnnouncementStatusEffect
  >(
    {
      articleTimestamp: null,
      articleUnreadRevision: 0,
      canReserveMeta: initial.canReserveMeta,
      hasReleasedMetaReservation: !initial.canReserveMeta,
      isMetaReady: false,
      readAnnouncements: new Map(),
      readRevision: initial.readRevision,
      readTimestamp: initial.readTimestamp,
    },
    (state, event) => {
      switch (event.type) {
        case "announcement/status-meta-synced":
          return {
            status: "handled",
            state: {
              ...state,
              articleTimestamp: event.articleTimestamp,
              articleUnreadRevision: event.articleUnreadRevision,
              canReserveMeta: event.canReserveMeta,
              isMetaReady: event.isMetaReady,
              hasReleasedMetaReservation:
                state.hasReleasedMetaReservation || !event.canReserveMeta,
            },
          };
        case "announcement/status-storage-synced":
          return {
            status: "handled",
            state: {
              ...state,
              readTimestamp: event.readTimestamp,
              readRevision: event.readRevision,
            },
          };
        case "announcement/read-requested":
          return markOneRead(state, event.announcement);
        case "announcement/read-through-requested":
          return markReadThrough(
            state,
            event.publishedAt,
            event.articleUnreadRevision,
          );
        case "announcement/current-list-read-requested":
          if (!state.isMetaReady || !Array.isArray(event.announcements)) {
            return { status: "unhandled" };
          }
          return markReadThrough(
            state,
            getFetchedAnnouncementsReadThroughTimestamp(event.announcements),
            getFetchedAnnouncementsReadThroughRevision(
              event.announcements,
              event.snapshotRevision,
            ),
          );
      }
    },
    { runEffect },
  );

  return Object.freeze({
    get isDisposed(): boolean {
      return mediator.isDisposed;
    },
    getSnapshot: mediator.getSnapshot,
    subscribe: mediator.subscribe,
    dispatch: mediator.dispatch,
    dispose: mediator.dispose,
    hasUnreadAnnouncements: (): boolean => {
      const state = mediator.getSnapshot();
      return state.articleUnreadRevision > 0
        ? isUnreadAnnouncementRevision(
            state.articleUnreadRevision,
            state.readRevision,
          )
        : isUnreadAnnouncement(state.articleTimestamp, state.readTimestamp);
    },
    isAnnouncementMetaPending: (): boolean => {
      const state = mediator.getSnapshot();
      return state.canReserveMeta && !state.hasReleasedMetaReservation;
    },
    isAnnouncementRead: (
      announcement: Pick<
        PublicAnnouncement,
        "id" | "published_at" | "article_unread_revision"
      >,
    ): boolean => isRead(mediator.getSnapshot(), announcement),
  });
}

function markOneRead(
  state: AnnouncementStatusState,
  announcement: Pick<
    PublicAnnouncement,
    "id" | "published_at" | "article_unread_revision"
  >,
) {
  const itemRevision = announcement.article_unread_revision ?? 0;
  if (isRead(state, announcement)) return { status: "unhandled" as const };
  const readAnnouncements = new Map(state.readAnnouncements);
  readAnnouncements.set(announcement.id, {
    publishedAt: announcement.published_at,
    articleUnreadRevision: itemRevision,
  });
  return {
    status: "handled" as const,
    state: { ...state, readAnnouncements },
  };
}

function markReadThrough(
  state: AnnouncementStatusState,
  publishedAt: string | null,
  articleUnreadRevision: number | null,
) {
  const readTimestamp = advanceAnnouncementReadTimestamp(
    state.readTimestamp,
    publishedAt,
  );
  const readRevision = advanceAnnouncementReadRevision(
    state.readRevision,
    articleUnreadRevision,
  );
  const effects: AnnouncementStatusEffect[] = [];
  const readAnnouncements = retainAnnouncementReadsAfter(
    state.readAnnouncements,
    publishedAt,
    articleUnreadRevision,
  );
  if (
    readTimestamp === state.readTimestamp &&
    readRevision === state.readRevision &&
    readAnnouncements === state.readAnnouncements
  ) {
    return { status: "unhandled" as const };
  }
  if (readTimestamp !== state.readTimestamp && readTimestamp !== null) {
    effects.push({ type: "persist-read-timestamp", value: readTimestamp });
  }
  if (readRevision !== state.readRevision && readRevision !== null) {
    effects.push({ type: "persist-read-revision", value: readRevision });
  }
  return {
    status: "handled" as const,
    state: {
      ...state,
      readTimestamp,
      readRevision,
      readAnnouncements,
    },
    effects,
  };
}

function isRead(
  state: AnnouncementStatusState,
  announcement: Pick<
    PublicAnnouncement,
    "id" | "published_at" | "article_unread_revision"
  >,
): boolean {
  const itemRevision = announcement.article_unread_revision ?? 0;
  const sessionRead = state.readAnnouncements.get(announcement.id);
  if (itemRevision > 0) {
    return (
      itemRevision <= (state.readRevision ?? 0) ||
      (sessionRead?.articleUnreadRevision ?? 0) >= itemRevision
    );
  }
  const publishedEpoch = announcementTimestampEpoch(announcement.published_at);
  if (publishedEpoch === null) return true;
  const readEpoch = announcementTimestampEpoch(state.readTimestamp);
  return (
    (readEpoch !== null && publishedEpoch <= readEpoch) ||
    sessionRead !== undefined
  );
}

function retainAnnouncementReadsAfter(
  readAnnouncements: ReadonlyMap<number, SessionAnnouncementRead>,
  publishedAt: string | null,
  articleUnreadRevision: number | null,
): ReadonlyMap<number, SessionAnnouncementRead> {
  const throughEpoch = announcementTimestampEpoch(publishedAt);
  if (
    (throughEpoch === null && articleUnreadRevision === null) ||
    readAnnouncements.size === 0
  ) {
    return readAnnouncements;
  }
  const retained = new Map<number, SessionAnnouncementRead>();
  for (const [id, receipt] of readAnnouncements) {
    const readEpoch = announcementTimestampEpoch(receipt.publishedAt);
    const covered =
      receipt.articleUnreadRevision > 0
        ? articleUnreadRevision !== null &&
          receipt.articleUnreadRevision <= articleUnreadRevision
        : throughEpoch !== null &&
          readEpoch !== null &&
          readEpoch <= throughEpoch;
    if (!covered) retained.set(id, receipt);
  }
  return retained.size === readAnnouncements.size
    ? readAnnouncements
    : retained;
}
